/**
 * Workflow review displays raw log values. These helpers format North American
 * numbers for phone-related field keys using libphonenumber-js (display-only).
 */

import parsePhoneNumberFromString, {
  getCountryCallingCode,
  type PhoneNumber,
} from 'libphonenumber-js';

/** Word-aware match for phone / phoneNumber / contactNumber (camelCase safe). */
export function isWorkflowReviewPhoneFieldKey(key: string): boolean {
  const lower = key.toLowerCase().replace(/_/g, '');
  if (lower.includes('phonenumber')) return true;
  if (lower.includes('contactnumber')) return true;

  const dehumped = key.replace(/([a-z])([A-Z])/g, '$1 $2');
  const tokens = dehumped
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

  return tokens.includes('phone');
}

function tryParseNanp(raw: string): PhoneNumber | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const digitsOnly = trimmed.replace(/\D/g, '');
  let candidate: string;

  if (trimmed.startsWith('+')) {
    candidate = trimmed;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    candidate = `+${digitsOnly}`;
  } else if (digitsOnly.length === 10) {
    candidate = `+1${digitsOnly}`;
  } else {
    return undefined;
  }

  return parsePhoneNumberFromString(candidate) ?? undefined;
}

/** NANP / ITU region 1 (+1) using lib metadata, not hand-rolled calling-code checks. */
function isParsedNorthAmericanNanp(parsed: PhoneNumber): boolean {
  if (!parsed.isValid() || parsed.nationalNumber.length !== 10) return false;
  if (parsed.country && getCountryCallingCode(parsed.country) === '1') {
    return true;
  }
  return parsed
    .getPossibleCountries()
    .some((code) => getCountryCallingCode(code) === '1');
}

/** True when the value parses as a valid NANP number. */
export function looksLikeNorthAmericanPhoneDigits(raw: string): boolean {
  const parsed = tryParseNanp(raw);
  return parsed ? isParsedNorthAmericanNanp(parsed) : false;
}

/**
 * Formats valid NANP values using libphonenumber-js parsing:
 * - Leading `+`, or 11 digits starting with `1` (E.164 without `+`): `(1)xxx-xxx-xxxx`
 * - 10-digit national storage only: `xxx-xxx-xxxx`
 * Returns the original string when parsing/validation fails.
 */
export function formatWorkflowReviewPhoneDisplay(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return raw;

  const digitsOnly = trimmed.replace(/\D/g, '');
  const showCountryOne =
    trimmed.startsWith('+') ||
    (digitsOnly.length === 11 && digitsOnly.startsWith('1'));

  const parsed = tryParseNanp(raw);

  if (!parsed || !isParsedNorthAmericanNanp(parsed)) {
    return raw;
  }

  const national = parsed.nationalNumber;
  const core = `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6, 10)}`;
  return showCountryOne ? `(1)${core}` : core;
}
